import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi } from '../services/api';
import { closeCheckout, onPaddleEvent, openCheckout } from '../services/paddle';
import { Plan } from '../lib/billing';
import { toaster } from '../components/ui';

export const PLANS_QUERY_KEY = ['plans'];

/** What's on sale. The backend caches Paddle for ~10 min; match it here. */
export const usePlans = () => {
  const { data, isLoading, isError } = useQuery<Plan[]>({
    queryKey: PLANS_QUERY_KEY,
    queryFn: paymentsApi.getPlans,
    staleTime: 10 * 60 * 1000,
  });
  const plans = data ?? [];
  return { plans, plan: plans[0] ?? null, isLoading, isError };
};

const detailOf = (err: any, fallback: string): string => err?.response?.data?.detail || err?.message || fallback;

/**
 * Checkout and the billing portal, shared by the paywall and the account screen.
 * `subscribe` opens Paddle's overlay over the current page; on completion we close
 * it and hand off to /subscription/success, which reconciles with `POST /sync`.
 */
export const useBilling = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      onPaddleEvent((event) => {
        if (event.name === 'checkout.completed') {
          void closeCheckout();
          navigate('/subscription/success', { replace: true });
        } else if (event.name === 'checkout.closed') {
          setBusy(false);
        }
      }),
    [navigate],
  );

  const subscribe = useCallback(
    async (plan = 'monthly'): Promise<void> => {
      setBusy(true);
      try {
        const { transaction_id } = await paymentsApi.createCheckout(plan);
        await openCheckout(transaction_id);
      } catch (err: any) {
        // A 409 means our view of the subscription is stale (bought elsewhere, or a
        // payment failed) — refresh so the button switches to the right action.
        if (err?.response?.status === 409) void refreshUser();
        toaster.error({ title: detailOf(err, 'Could not start checkout') });
        setBusy(false);
      }
    },
    [refreshUser],
  );

  const openPortal = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const { portal_url } = await paymentsApi.createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      toaster.error({ title: detailOf(err, 'Could not open the billing portal') });
      setBusy(false);
    }
  }, []);

  return { busy, subscribe, openPortal };
};
